import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface SearchTweet {
  tweetId: string;
  tweetUrl: string;
  text: string;
  createdAt: string;
  lang: string;
  authorId: string;
  authorUsername: string;
  authorName: string;
  authorProfilePicture: string;
  authorIsBlueVerified: boolean;
  authorFollowers: number;
  likeCount: number;
  retweetCount: number;
  replyCount: number;
  quoteCount: number;
  viewCount: number;
  isReply: boolean;
  inReplyToUsername: string | null;
}

/**
 * Fetch tweets via x-search-tweets action proxy.
 *
 * Reads `_twitterQuery` from triggerPayload — this is injected by
 * HistoricalPicker's Twitter filter form before calling loadData().
 * The query uses Twitter advanced search syntax (e.g., "from:elonmusk tesla").
 */
export const twitterSearchFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const query = ctx.triggerPayload._twitterQuery as string | undefined;
  if (!query) return [];

  const response = await authenticatedFetch<{
    code: number;
    data: { result: { tweets: SearchTweet[]; totalResults: number; hasNextPage: boolean }; success: boolean };
  }>(`${V2_ENDPOINT}/v1/action-proxy/x-search-tweets/query`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payload: { query, queryType: "Latest" } }),
  });

  if (!response.ok) throw new Error(`Twitter search failed (HTTP ${response.status})`);
  const data = await response.json();
  if (data.code !== 0 || !data.data?.success) {
    throw new Error(data.message ?? "Twitter search returned an error");
  }

  const tweets = data.data.result?.tweets ?? [];

  return tweets.map((tweet: SearchTweet) => ({
    // Fields matching x-new-tweet trigger resultSchema
    triggeredAt: tweet.createdAt || new Date().toISOString(),
    tweetId: tweet.tweetId,
    tweetUrl: tweet.tweetUrl,
    text: tweet.text,
    createdAt: tweet.createdAt,
    lang: tweet.lang,
    authorId: tweet.authorId,
    authorUsername: tweet.authorUsername,
    authorName: tweet.authorName,
    authorProfilePicture: tweet.authorProfilePicture,
    authorIsBlueVerified: tweet.authorIsBlueVerified,
    authorFollowers: tweet.authorFollowers,
    likeCount: tweet.likeCount,
    retweetCount: tweet.retweetCount,
    replyCount: tweet.replyCount,
    quoteCount: tweet.quoteCount,
    viewCount: tweet.viewCount,
    isReply: tweet.isReply,
    inReplyToUsername: tweet.inReplyToUsername,
    // Metadata for HistoricalPicker UI (prefixed with _ so shell strips them)
    _text: tweet.text,
    _authorUsername: tweet.authorUsername,
    _authorName: tweet.authorName,
    _authorProfilePicture: tweet.authorProfilePicture,
    _authorIsBlueVerified: tweet.authorIsBlueVerified,
    _likeCount: tweet.likeCount,
    _retweetCount: tweet.retweetCount,
  }));
};
